import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ChatHistoryMessage, ChatHistoryRequest } from '../models/chat-message.model';
import { environment } from '../../../environments/environment';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';

export interface ChatbotFAQ {
  idChatbotFAQ: number;
  Question: string;
  Answer: string;
  Category: string | null;
  SortOrder: number;
  IsActive: number;
  DateStart?: string;
  DateEnd?: string;
}

export interface ChatbotCarouselCard {
  idChatbotCarouselProduct: number;
  idProductsTypes: number | null;
  Title: string;
  Description: string | null;
  ImageURL: string | null;
  ActionURL: string | null;
  ActionLabel: string;
  SortOrder: number;
  IsActive: number;
  Channel: 'all' | 'site' | 'whatsapp';
  ProductName?: string;
  ProductSlug?: string;
  ProductIsForSale?: number;
  ProductShowInHomeSearch?: number;
  DateStart?: string;
  DateEnd?: string;
}

export interface AITokenKPIs {
  tokensToday: number;
  tokensThisMonth: number;
  tokensTotal: number;
  costTodayEUR: number;
  costThisMonthEUR: number;
  costTotalEUR: number;
  callsToday: number;
  callsThisMonth: number;
  callsTotal: number;
  mostActiveModule: string;
  mostUsedModel: string;
}

export interface AITokenDailyTrend {
  date: string;
  dateFormatted: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costEUR: number;
  callsCount: number;
}

export interface AITokenMonthlyTrend {
  monthYear: string;
  monthFormatted: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costEUR: number;
  callsCount: number;
}

export interface AITokenModuleDist {
  module: string;
  totalTokens: number;
  costEUR: number;
  callsCount: number;
  percentage: number;
}

export interface AITokenModelDist {
  aiModel: string;
  totalTokens: number;
  costEUR: number;
  callsCount: number;
  percentage: number;
}

export interface AITokenStatsResponse {
  success: boolean;
  kpis: AITokenKPIs;
  dailyTrend: AITokenDailyTrend[];
  monthlyTrend: AITokenMonthlyTrend[];
  moduleDistribution: AITokenModuleDist[];
  modelDistribution: AITokenModelDist[];
}

export interface AITokenLogItem {
  Id: number;
  Module: string;
  Action: string;
  AIModel: string;
  PromptTokens: number;
  CompletionTokens: number;
  TotalTokens: number;
  EstimatedCostEUR: number;
  ReferenceId: string | null;
  IdUser: number | null;
  UserName: string;
  SessionId: string | null;
  Status: string;
  CreatedAtFormatted: string;
  CreatedAt: string;
}

export interface AITokenLogsResponse {
  success: boolean;
  data: AITokenLogItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ChatbotGeneralSettings {
  Id: number;
  AgentName: string;
  PersonaDescription: string | null;
  SystemPrompt: string;
  ToneOfVoice: string;
  ActiveModel: string;
  Temperature: number;
  MaxTokens: number;
  FallbackMessage: string | null;
  IsActive?: number;
}

export interface ChatbotDocumentSettings {
  Id: number;
  Title: string;
  ActiveModel: string;
  SystemPrompt: string;
  Temperature: number;
  MaxTokens: number;
  FailoverEnabled: number;
  SupportedDocuments?: string;
  IsActive?: number;
}

export interface ChatbotPolicySettings {
  Id: number;
  Title: string;
  ActiveModel: string;
  SystemPrompt: string;
  Temperature: number;
  MaxTokens: number;
  FailoverEnabled: number;
  SupportedDocuments?: string;
  IsActive?: number;
}

export interface ChatbotConversationSummary {
  SessionId: string;
  Contact: string | null;
  NIF: string | null;
  IdUser: number | null;
  DisplayName: string;
  Channel: string;
  TotalMessages: number;
  LastUserMessage: string | null;
  LastMessageAt: string;
  StartedAt: string;
  HasUnanswered: number;
}

export interface ChatbotConversationMessageItem {
  Id: number;
  SessionId: string;
  Sender: 'user' | 'bot';
  Message: string;
  Contact: string | null;
  NIF: string | null;
  IdUser: number | null;
  Channel: string;
  IsUnanswered: number;
  CreatedAt: string;
  CreatedAtFormatted: string;
}

export interface ChatbotUnansweredQuestionItem {
  Id: number;
  SessionId: string;
  Question: string;
  Contact: string | null;
  NIF: string | null;
  Channel: string;
  CreatedAt: string;
  CreatedAtFormatted: string;
}

/**
 * Serviço para comunicação com a API do chatbot Ricardo e CRUD de configuração no CRM.
 *
 * Em dev, usa proxy local (/webhook/api) para evitar CORS no chat real.
 * Usa o environment.URL normal para as queries à API própria (faqs e configs).
 *
 * appDevTag="shared/services/chatbot.service"
 */
@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private readonly BOT_URL = environment.chatbotUrl;
  private readonly API_URL = `${environment.URL}/chatbot`;
  private readonly botApiKey = 'agente_dev_HBBO3BlbkFJa6bidWLzt0ordanR7tmo'; // Esta chave está na versão anterior

  constructor(private http: HttpClient) {}

  /**
   * Cria os headers necessários para cada chamada à API do motor de chat Python/n8n.
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.botApiKey,
    });
  }

  /**
   * Logging completo de erro
   */
  private logError(endpoint: string, error: HttpErrorResponse): void {
    const separator = '════════════════════════════════════════';
    console.error(`\n${separator}`);
    console.error(`🤖 [Chatbot] ERRO em ${endpoint}`);
    console.error(separator);
    console.error('📍 URL pedido: ' + error.url);
    console.error('📊 HTTP Status: ' + error.status + ' (' + error.statusText + ')');

    if (error.status === 0) {
      console.error('🚫 CORS ou erro de rede bloqueou o pedido');
    }
  }

  /**
   * Logging request
   */
  private logRequest(endpoint: string, payload: any): void {
    console.log(`\n🤖 [Chatbot] REQUEST → ${endpoint}\nPayload:`, payload);
  }

  // ==========================================
  // CONFIGURAÇÃO (CRM / MYSQL API PRÓPRIA)
  // ==========================================

  getFAQs(): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/faqs`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setFAQ(faq: Partial<ChatbotFAQ>): Observable<any> {
    return this.http
      .post<any>(`${this.API_URL}/faqs`, {
        Id: faq.idChatbotFAQ || 0,
        Question: faq.Question,
        Answer: faq.Answer,
        Category: faq.Category,
        SortOrder: faq.SortOrder || 0,
        IsActive: faq.IsActive === 0 ? false : true,
      })
      .pipe(
        catchError(error => {
          const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  deleteFAQ(id: number): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/faqs/${id}`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ==========================================
  // CONFIGURAÇÃO CARROSSEL (CRM)
  // ==========================================

  getCarouselCards(): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/carousel`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setCarouselCard(card: Partial<ChatbotCarouselCard>): Observable<any> {
    return this.http
      .post<any>(`${this.API_URL}/carousel`, {
        Id: card.idChatbotCarouselProduct || 0,
        idProductsTypes: card.idProductsTypes || null,
        Title: card.Title,
        Description: card.Description,
        ImageURL: card.ImageURL,
        ActionURL: card.ActionURL,
        ActionLabel: card.ActionLabel || 'Simular Agora',
        SortOrder: card.SortOrder || 0,
        IsActive: card.IsActive === 0 ? false : true,
        Channel: card.Channel || 'all',
      })
      .pipe(
        catchError(error => {
          const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  deleteCarouselCard(id: number): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/carousel/${id}`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ==========================================
  // PARAMETRIZAÇÃO IA (CRM)
  // ==========================================

  getChatbotSettings(): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/settings`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setChatbotSettings(settings: any): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/settings`, settings).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  deleteChatbotSettings(id: number): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/settings/${id}`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ==========================================
  // PARAMETRIZAÇÃO IA - SINISTROS (CRM)
  // ==========================================

  getClaimsAISettings(): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/claims-settings`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setClaimsAISettings(settings: any): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/claims-settings`, settings).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  deleteClaimsAISettings(id: number): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/claims-settings/${id}`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ==========================================
  // CONSUMO DE TOKENS & AUDITORIA IA (Issue #261)
  // ==========================================

  getAITokenStats(days: number = 30, module?: string | null): Observable<AITokenStatsResponse> {
    let params: any = { days };
    if (module) params.module = module;
    return this.http.get<AITokenStatsResponse>(`${this.API_URL}/token-stats`, { params }).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  getAITokenLogs(
    page: number = 1,
    pageSize: number = 20,
    module?: string | null,
    model?: string | null,
    search?: string | null,
    startDate?: string | null,
    endDate?: string | null,
  ): Observable<AITokenLogsResponse> {
    let params: any = { page, pageSize };
    if (module) params.module = module;
    if (model) params.model = model;
    if (search) params.search = search;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    return this.http.get<AITokenLogsResponse>(`${this.API_URL}/token-logs`, { params }).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  logAITokenUsage(data: Partial<AITokenLogItem>): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/log-usage`, data).pipe(catchError(() => of(null)));
  }

  // ==========================================
  // MOTOR DE CHATBOT PYTHON / N8N
  // ==========================================

  sendMessage(number: number, message: string, nif?: string): Observable<{ message: string }> {
    const payload = { number, message, nif: nif || undefined };
    this.logRequest('/chat/request', payload);

    return this.http
      .post<{ message: string }>(`${this.BOT_URL}/chat/request`, payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap(() => console.log('🤖✅ Resposta enviada com sucesso')),
        catchError((error: HttpErrorResponse) => {
          this.logError('/chat/request', error);
          return throwError(() => error);
        }),
      );
  }

  registerUser(number: number, nif?: string): Observable<any> {
    const payload = { number, nif: nif || undefined };
    this.logRequest('/register_user', payload);

    return this.http
      .post<any>(`${this.BOT_URL}/register_user`, payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.logError('/register_user', error);
          return throwError(() => error);
        }),
      );
  }

  updateUser(oldNumber: number, newNumber: number): Observable<any> {
    const payload = { old_number: oldNumber, number: newNumber };
    return this.http
      .post<any>(`${this.BOT_URL}/update_user`, payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.logError('/update_user', error);
          return throwError(() => error);
        }),
      );
  }

  deleteUser(number: number): Observable<any> {
    const payload = { number };
    return this.http.post<any>(`${this.BOT_URL}/delete_user`, payload, {
      headers: this.getHeaders(),
    });
  }

  getUserHistory(number: number, nif: string, agentName: string, startDate: string): Observable<ChatHistoryMessage[]> {
    const payload: ChatHistoryRequest = { number, nif, agent_name: agentName, date: startDate };
    return this.http.post<ChatHistoryMessage[]>(`${this.BOT_URL}/user_history`, payload, {
      headers: this.getHeaders(),
    });
  }

  // ==========================================
  // CONFIGURAÇÕES GERAIS E PERSONALIDADE (Issue #276)
  // ==========================================
  getGeneralSettings(): Observable<{ success: boolean; data: ChatbotGeneralSettings }> {
    return this.http.get<{ success: boolean; data: ChatbotGeneralSettings }>(`${this.API_URL}/general-settings`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setGeneralSettings(settings: Partial<ChatbotGeneralSettings>): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/general-settings`, settings).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ==========================================
  // PARAMETRIZAÇÃO IA - LEITURA DE DOCUMENTOS & CC
  // ==========================================
  getDocumentSettings(): Observable<{ success: boolean; data: ChatbotDocumentSettings }> {
    return this.http.get<{ success: boolean; data: ChatbotDocumentSettings }>(`${this.API_URL}/document-settings`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setDocumentSettings(settings: Partial<ChatbotDocumentSettings>): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/document-settings`, settings).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ==========================================
  // PARAMETRIZAÇÃO IA - LEITURA E CONVERSÃO DE APÓLICES
  // ==========================================
  getPolicySettings(): Observable<{ success: boolean; data: ChatbotPolicySettings }> {
    return this.http.get<{ success: boolean; data: ChatbotPolicySettings }>(`${this.API_URL}/policy-settings`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setPolicySettings(settings: Partial<ChatbotPolicySettings>): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/policy-settings`, settings).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  getPolicyAIRules(): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/policy-rules`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setPolicyAIRule(rule: any): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/policy-rules`, rule).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  deletePolicyAIRule(id: number): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/policy-rules/${id}`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ==========================================
  // SESSÕES DE CONVERSA E MENSAGENS (Issue #276)
  // ==========================================
  getConversations(
    page: number = 1,
    pageSize: number = 15,
    search?: string,
    startDate?: string,
    endDate?: string,
    channel?: string,
  ): Observable<{ success: boolean; data: ChatbotConversationSummary[]; total: number }> {
    const params: any = { page, pageSize };
    if (search) params.search = search;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (channel && channel !== 'all') params.channel = channel;

    return this.http
      .get<{
        success: boolean;
        data: ChatbotConversationSummary[];
        total: number;
      }>(`${this.API_URL}/conversations`, { params })
      .pipe(
        catchError(error => {
          const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  getConversationMessages(sessionId: string): Observable<{ success: boolean; data: ChatbotConversationMessageItem[] }> {
    return this.http
      .get<{ success: boolean; data: ChatbotConversationMessageItem[] }>(`${this.API_URL}/conversations/${sessionId}`)
      .pipe(
        catchError(error => {
          const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  logChatMessage(data: {
    sessionId: string;
    sender: 'user' | 'bot';
    message: string;
    contact?: string;
    nif?: string;
    idUser?: number;
    channel?: string;
    isUnanswered?: boolean;
  }): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/log-message`, data).pipe(catchError(() => of(null)));
  }

  getUnansweredQuestions(limit: number = 20): Observable<{ success: boolean; data: ChatbotUnansweredQuestionItem[] }> {
    return this.http
      .get<{
        success: boolean;
        data: ChatbotUnansweredQuestionItem[];
      }>(`${this.API_URL}/unanswered-questions`, { params: { limit } })
      .pipe(
        catchError(error => {
          const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }
}
